import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Loader,
} from "@devhop/ui";
import { nowISO } from "@/shared/lib/date-time";
import { useNavigate } from "@tanstack/react-router";
import { Monitor, Store } from "lucide-react";
import { useState } from "react";
import { useBranchDevices, useUserBranches } from "../api/queries";
import { usePOSContext } from "../context/POSContext";

export function BranchDeviceSelectionPage() {
  const navigate = useNavigate();
  const { setSelection } = usePOSContext();
  const [selectedBranchId, setSelectedBranchId] = useState<string | null>(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [selectedDeviceName, setSelectedDeviceName] = useState<string | null>(
    null,
  );

  const { data: branches, isLoading: branchesLoading } = useUserBranches();
  const { data: devices, isLoading: devicesLoading } =
    useBranchDevices(selectedBranchId);

  const handleBranchSelect = (branchId: string) => {
    setSelectedBranchId(branchId);
    setSelectedDeviceId(null);
    setSelectedDeviceName(null);
  };

  const handleDeviceSelect = (deviceId: string, deviceName: string) => {
    setSelectedDeviceId(deviceId);
    setSelectedDeviceName(deviceName);
  };

  const handleConfirm = () => {
    if (
      !selectedBranchId ||
      !selectedDeviceId ||
      !selectedDeviceName ||
      !branches
    )
      return;

    const branch = branches.find((b) => b.id === selectedBranchId);
    if (!branch) return;

    setSelection({
      branchId: selectedBranchId,
      branchName: branch.name,
      deviceId: selectedDeviceId,
      deviceName: selectedDeviceName,
      selectedAt: nowISO(),
    });

    navigate({ to: "/app/pos" });
  };

  const canProceed =
    selectedBranchId && selectedDeviceId && selectedDeviceName;

  if (branchesLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!branches || branches.length === 0) {
    return (
      <div className="flex h-full items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>ບໍ່ພົບສາຂາ</CardTitle>
            <CardDescription>
              ທ່ານບໍ່ໄດ້ຖືກມອບໝາຍໃຫ້ກັບສາຂາໃດໆ. ກະລຸນາຕິດຕໍ່ຜູ້ຈັດການລະບົບ.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto h-full max-w-6xl p-6">
      <div className="mb-8">
        <h1 className="mb-2 font-bold text-3xl">ເລືອກສາຂາແລະເຄື່ອງ POS</h1>
        <p className="text-lg text-muted-foreground">
          ກະລຸນາເລືອກສາຂາແລະເຄື່ອງ POS ທີ່ທ່ານຈະໃຊ້ງານ
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Branch Selection */}
        <div>
          <h2 className="mb-4 font-semibold text-xl">1. ເລືອກສາຂາ</h2>
          <div className="grid gap-4">
            {branches.map((branch) => (
              <Card
                key={branch.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  selectedBranchId === branch.id
                    ? "border-primary ring-2 ring-primary"
                    : ""
                }`}
                onClick={() => handleBranchSelect(branch.id)}
              >
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Store className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg">{branch.name}</CardTitle>
                  </div>
                  {branch.code && (
                    <CardDescription>ລະຫັດ: {branch.code}</CardDescription>
                  )}
                </CardHeader>
                {branch.addressLine1 && (
                  <CardContent>
                    <p className="text-muted-foreground text-sm">
                      {branch.addressLine1}
                      {branch.district && `, ${branch.district}`}
                      {branch.province && `, ${branch.province}`}
                    </p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* Device Selection */}
        <div>
          <h2 className="mb-4 font-semibold text-xl">2. ເລືອກເຄື່ອງ POS</h2>
          {!selectedBranchId ? (
            <Card>
              <CardContent className="flex h-48 items-center justify-center">
                <p className="text-center text-muted-foreground">
                  ກະລຸນາເລືອກສາຂາກ່ອນ
                </p>
              </CardContent>
            </Card>
          ) : devicesLoading ? (
            <Card>
              <CardContent className="flex h-48 items-center justify-center">
                <Loader />
              </CardContent>
            </Card>
          ) : !devices || devices.length === 0 ? (
            <Card>
              <CardContent className="flex h-48 items-center justify-center">
                <p className="text-center text-muted-foreground">
                  ບໍ່ພົບເຄື່ອງ POS ທີ່ພ້ອມໃຊ້ງານໃນສາຂານີ້
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {devices.map((device) => (
                <Card
                  key={device.id}
                  className={`cursor-pointer transition-all hover:shadow-md ${
                    selectedDeviceId === device.id
                      ? "border-primary ring-2 ring-primary"
                      : ""
                  }`}
                  onClick={() => handleDeviceSelect(device.id, device.name)}
                >
                  <CardHeader>
                    <div className="flex items-center gap-2">
                      <Monitor className="h-5 w-5 text-primary" />
                      <CardTitle className="text-lg">{device.name}</CardTitle>
                    </div>
                    {device.code && (
                      <CardDescription>ລະຫັດ: {device.code}</CardDescription>
                    )}
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
          {selectedBranchId && selectedDeviceId && (
            <div className="mt-6">
              <Button
                className="w-full"
                onClick={handleConfirm}
                disabled={!canProceed}
              >
                ຢືນຢັນແລະເຂົ້າສູ່ລະບົບ
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
